import { Input } from '@agavi/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

import LeftPanel from '@/components/auth/left-panel';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { api } from '@/lib/api';
import { forgotPasswordSchema, type ForgotPasswordFormValues } from '@/types/auth';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  async function onSubmit(data: ForgotPasswordFormValues) {
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', data);
      navigate('/confirm', { state: { flow: 'reset', email: data.email } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className='flex min-h-screen bg-ws-bg'>
      <LeftPanel />

      <div className='flex flex-1 items-center justify-center px-6'>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='w-full max-w-[320px] space-y-8'
          >
            {/* Back */}
            <Link
              to='/login'
              className='flex items-center gap-1 text-sm font-medium text-ws-text hover:underline'
            >
              <ChevronLeft className='w-4 h-4' />
              {t('common.back')}
            </Link>

            {/* Header */}
            <div className='text-center space-y-2'>
              <h1 className='text-3xl font-semibold text-ws-text'>
                {t('auth.forgotPassword.title')}
              </h1>
              <p className='text-base text-ws-muted'>
                {t('auth.forgotPassword.subtitle')}
              </p>
            </div>

            {/* Fields */}
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-base font-medium'>
                    {t('auth.forgotPassword.email')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type='email'
                      placeholder={t('auth.forgotPassword.emailPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <p className='text-xs text-ws-muted'>
                    {t('auth.forgotPassword.emailHint')}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Error */}
            {error && <p className='text-sm text-ws-error-fg text-center'>{error}</p>}

            {/* Actions */}
            <Button type='submit' variant='primary' size='lg' block busy={loading}>
              {loading ? t('auth.forgotPassword.submitting') : t('auth.forgotPassword.submit')}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
